import { useMutation } from '@apollo/client';
import { Idea, REPORT_IDEA } from '@frontend/data-access';
import { App, Button, Checkbox, Form, Input } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useMemo, useState } from 'react';

import styles from './idea-report.module.less';

/* eslint-disable-next-line */
export interface IdeaReportProps {
  data: Idea;
  onCancel(): void;
}

export function IdeaReport({ data, onCancel }: IdeaReportProps) {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [isIncomplete, setIsIncomplete] = useState(false);
  const { t } = useTranslation('common');

  const options = useMemo(
    () => [
      {
        label: t('harassment'),
        value: 'HARASSMENT',
      },
      {
        label: t('nudity'),
        value: 'NUDITY',
      },
      {
        label: t('hate_speech'),
        value: 'HATE_SPEECH',
      },
      {
        label: t('other_reasons'),
        value: 'OTHER',
      },
    ],
    [t]
  );

  const handleCompleted = () => {
    form.resetFields();
    onCancel();
    notification.success({
      message: t('report_success_message'),
    });
  };

  const [reportIdea, { loading }] = useMutation(REPORT_IDEA, {
    onCompleted: handleCompleted,
  });

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setIsIncomplete(hasErrors);
  };

  const handleOnFinish = useCallback(
    async (values: any) => {
      try {
        await form.validateFields();
        await reportIdea({
          variables: {
            input: {
              ...values,
              ideaId: data?.id,
            },
          },
        });
      } catch (error) {
        //
      }
    },
    [data?.id, form, reportIdea]
  );

  return (
    <div className={styles['container']}>
      <Form
        form={form}
        layout="vertical"
        requiredMark={'optional'}
        onFieldsChange={handleFormChange}
        onFinish={handleOnFinish}
      >
        <Form.Item
          name="reportStatus"
          label={t('problem_label')}
          rules={[{ required: true, message: t('problem_required_message') }]}
        >
          <Checkbox.Group data-test="idea-report-checkbox-group">
            {options.map((item) => (
              <div key={item.value}>
                <Checkbox value={item.value}>{item.label}</Checkbox>
              </div>
            ))}
          </Checkbox.Group>
        </Form.Item>
        <Form.Item name="description" label={t('description_label')}>
          <Input.TextArea
            rows={4}
            placeholder={t('description_placeholder')}
            data-test="idea-report-description"
          />
        </Form.Item>
        <div className="flex justify-end items-center gap-2">
          <Button key="back" shape="round" onClick={() => onCancel()}>
            {t('cancel_button')}
          </Button>
          <Button
            type="primary"
            shape="round"
            loading={loading}
            disabled={isIncomplete}
            htmlType="submit"
            data-test="idea-report-submit"
          >
            {t('submit_button')}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default IdeaReport;
